import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, ShoppingBag, Search } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import LooxsLogo from "@/components/LooxsLogo";

const NAV = [
  { to: "/chapters", label: "Chapters" },
  { to: "/collections", label: "Collections" },
  { to: "/campaigns", label: "Campaigns" },
  { to: "/world", label: "World" },
  { to: "/about", label: "About" },
  { to: "/careers", label: "Careers" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Header({ variant = "light" }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const { count, setOpen: openBag } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isDark = false; // LOOXS is a cream brand — header is always cream-backed
  const bg = scrolled ? "bg-ivory/95 backdrop-blur-lg text-ink" : "bg-ivory/90 backdrop-blur-md text-ink";
  const borderCls = scrolled ? "border-b border-black/10" : "";

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-40 ${bg} ${borderCls} transition-colors duration-500`}>
        <div className="grid grid-cols-3 items-center h-24 md:h-28 px-6 md:px-10">
          {/* Left: menu */}
          <div className="flex items-center gap-6">
            <button data-testid="menu-open" onClick={() => setOpen(true)} className="flex items-center gap-3 tracking-widest-plus text-[11px] uppercase" aria-label="Open menu">
              <Menu className="w-4 h-4" strokeWidth={1.25} /> Menu
            </button>
            <Link to="/collections" data-testid="nav-search" aria-label="Search" className="hidden md:flex items-center gap-2 tracking-widest-plus text-[11px] uppercase">
              <Search className="w-4 h-4" strokeWidth={1.25} /> Search
            </Link>
          </div>

          {/* Center: OFFICIAL LOOXS LOGO — supplied brand asset, never re-typed */}
          <Link to="/" data-testid="brand-logo" aria-label="LOOXS home" className="justify-self-center block">
            <LooxsLogo height={scrolled ? 44 : 60} />
          </Link>

          {/* Right: account + bag */}
          <div className="flex items-center justify-end gap-6">
            <Link to={user ? "/account" : "/login"} data-testid="nav-account" className="hidden md:block tracking-widest-plus text-[11px] uppercase link-draw">
              {user ? "Account" : "Sign in"}
            </Link>
            {user?.role && ["staff","manager","owner"].includes(user.role) && (
              <Link to="/admin" data-testid="nav-admin" className="hidden md:block tracking-widest-plus text-[11px] uppercase link-draw">Admin</Link>
            )}
            <button data-testid="cart-open-btn" onClick={() => openBag(true)} className="relative flex items-center gap-2 tracking-widest-plus text-[11px] uppercase" aria-label="Open bag">
              <ShoppingBag className="w-4 h-4" strokeWidth={1.25} />
              <span>Bag</span>
              {count > 0 && <span data-testid="cart-count" className="text-[10px]">({count})</span>}
            </button>
          </div>
        </div>
      </header>

      {open && <MenuOverlay onClose={() => setOpen(false)} user={user} />}
    </>
  );
}

function MenuOverlay({ onClose, user }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);
  return (
    <div className="fixed inset-0 z-50 bg-ivory text-ink animate-fade-in" data-testid="menu-overlay">
      <div className="grid grid-cols-3 items-center h-24 md:h-28 px-6 md:px-10 border-b border-black/10">
        <button data-testid="menu-close" onClick={onClose} className="flex items-center gap-3 tracking-widest-plus text-[11px] uppercase">
          <X className="w-4 h-4" strokeWidth={1.25} /> Close
        </button>
        <span className="justify-self-center"><LooxsLogo height={48} /></span>
        <span className="justify-self-end tracking-widest-plus text-[11px] uppercase opacity-70">London · India</span>
      </div>
      <div className="max-w-6xl mx-auto px-8 md:px-16 py-16 grid md:grid-cols-2 gap-16">
        <nav className="space-y-4">
          {NAV.map((n, i) => (
            <Link key={n.to} to={n.to} onClick={onClose} data-testid={`menu-link-${n.label.toLowerCase()}`}
              className="flex items-baseline gap-3 leading-none tracking-widest-plus uppercase hover:text-gold transition-colors duration-500 animate-fade-up"
              style={{ animationDelay: `${i * 40}ms`, fontSize: "13px" }}>
              <span className="opacity-40 tabular-nums" style={{ fontSize: "11px" }}>0{i+1}</span>
              <span>{n.label}</span>
            </Link>
          ))}
        </nav>
        <div className="text-sm text-ink/70 max-w-md self-end">
          <div className="rule-gold w-16 mb-6" />
          <p className="tracking-widest-plus uppercase text-[11px] mb-3 text-goldDark">Concierge</p>
          <p className="font-editorial text-xl leading-relaxed text-ink mb-6">A LOOXS advisor is available for private appointments, styling and pre-order enquiries.</p>
          <p>care@looxsfashion.com</p>
          <p>Mon–Sat, 10:00 – 20:00 GMT</p>
          <div className="mt-10 pt-6 border-t border-black/10">
            <Link to={user ? "/account" : "/login"} onClick={onClose} className="tracking-widest-plus text-[11px] uppercase link-draw">
              {user ? "My account" : "Sign in / register"}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
